import { Paper, styled, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from '@mui/material';
import Loader from 'Components/ScopedLoader';
import PropTypes from 'prop-types';
import React from 'react';
import TablePaginationActions from './TablePaginationActions';

const StyledPaper = styled(Paper)(() => ({
    boxShadow: 'none',
    marginTop: 5,
    width: '100%'
}));

const StyledTable = styled(Table)(() => ({
    minWidth: 500
}));

const StyledTableRowHeader = styled(TableRow)(() => ({
    height: '38px'
}));

const StyledTableRow = styled(TableRow)(() => ({
    height: '30px',
    '&:hover': {
        background: '#EEEEEE'
    }
}));

// The same room on every side, and the same in the header as in the body, so a title sits
// over what it names.
const StyledTableCellHeader = styled(TableCell)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    fontSize: '0.80rem',
    padding: 5
}));

const StyledTableCell = styled(TableCell)(() => ({
    fontSize: '0.65rem',
    padding: 5
}));

const StyledTableWrapper = styled('div')(() => ({
    overflowX: 'auto'
}));

const Component = ({
    columns,
    disableScrollbar = false,
    loading,
    onRowClick,
    rows,
    rowsPerPage: initialRowsPerPage = 100,
    rowHeight,
    headerHeight,
    selectedRowId
}) => {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(initialRowsPerPage);

    if (loading) {
        return <Loader/>;
    }

    const fixedLayout = columns.some(column => column.width);
    const Columns = columns.map(column => (
        <StyledTableCellHeader key={column.label} style={column.width ? { width: column.width } : undefined}>
            {column.label}
        </StyledTableCellHeader>
    ));

    const Rows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => {
        const onClick = onRowClick ? () => onRowClick(row.id, row) : null;
        const isSelected = selectedRowId !== undefined && selectedRowId !== null && row.id === selectedRowId;
        const rowStyle = {
            cursor: onRowClick ? 'pointer' : 'auto',
            ...(rowHeight ? { height: rowHeight } : {}),
            ...(isSelected ? { backgroundColor: '#e8f5e9' } : {})
        };

        return (
            <StyledTableRow key={row.id} onClick={onClick} style={rowStyle}>
                {columns.map(column => {
                    let value = column.transform ? column.transform(row) : row[column.dataKey];
                    return (
                        <StyledTableCell key={column.dataKey} style={column.width ? { width: column.width } : undefined}>
                            {value !== undefined && value !== null ? value : ''}
                        </StyledTableCell>
                    );
                })}
            </StyledTableRow>
        );
    });

    return (
        <StyledPaper>
            <StyledTableWrapper style={disableScrollbar ? { overflow: 'hidden' } : undefined}>
                <StyledTable style={fixedLayout ? { tableLayout: 'fixed' } : undefined}>
                    <TableHead>
                        <StyledTableRowHeader style={headerHeight ? { height: headerHeight } : undefined}>
                            {Columns}
                        </StyledTableRowHeader>
                    </TableHead>
                    <TableBody>{Rows}</TableBody>
                    {rows.length > rowsPerPage && (
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    ActionsComponent={TablePaginationActions}
                                    colSpan={3}
                                    count={rows.length}
                                    onPageChange={(event, newPage) => setPage(newPage)}
                                    onRowsPerPageChange={event => {
                                        setRowsPerPage(parseInt(event.target.value, 10));
                                        setPage(0);
                                    }}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    rowsPerPageOptions={[rowsPerPage]}
                                    slotProps={{
                                        select: {
                                            inputProps: { 'aria-label': 'rows per page' },
                                            native: true
                                        }
                                    }}
                                />
                            </TableRow>
                        </TableFooter>
                    )}
                </StyledTable>
            </StyledTableWrapper>
        </StyledPaper>
    );
};

Component.propTypes = {
    columns: PropTypes.array.isRequired,
    disableScrollbar: PropTypes.bool,
    headerHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onRowClick: PropTypes.func,
    rowHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selectedRowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rows: PropTypes.array.isRequired,
    rowsPerPage: PropTypes.number,
    loading: PropTypes.bool
};

export default Component;
